import React from "react";
import { ControlPosition, Map, MapControl } from "@vis.gl/react-google-maps";
import { Point, Polygon } from "geojson";

import { useDrawingManager } from "./useDrawingManager";

export type DrawSinglePolygonMapHandle = {
  clearPolygon: () => void;
};

type Props = {
  defaultCenter: Point;
  setPolygon: React.Dispatch<React.SetStateAction<Polygon | null>>;
};

const DrawSinglePolygonMap = React.forwardRef<
  DrawSinglePolygonMapHandle,
  Props
>((props, ref) => {
  /**
   * A Google Maps component that allows drawing a single polygon.
   * Passes the drawn polygon's coordinates to the `setPolygon` state action,
   * for use outside of this function.
   */
  const drawingManager = useDrawingManager();

  const polygonRef = React.useRef<google.maps.Polygon | null>(null);

  React.useImperativeHandle(
    ref,
    () => ({
      /**
       * Clear the drawn polygon from the map,
       * and expose the function to the parent component.
       */
      clearPolygon() {
        if (polygonRef.current) {
          polygonRef.current.setMap(null);
          polygonRef.current = null;
          drawingManager?.setOptions({
            drawingControl: true,
            drawingMode: google.maps.drawing.OverlayType.POLYGON,
          });
          props.setPolygon(null);
        }
      },
    }),
    [drawingManager, props],
  );

  React.useEffect(() => {
    if (drawingManager) {
      const overlayCompleteListener = google.maps.event.addListener(
        drawingManager,
        "overlaycomplete",
        (e: google.maps.drawing.OverlayCompleteEvent) => {
          if (e.type === google.maps.drawing.OverlayType.POLYGON) {
            // Store the polygon.
            const _polygon = e.overlay as google.maps.Polygon;
            polygonRef.current = _polygon;
            // Disable further drawing.
            drawingManager.setDrawingMode(null);
            drawingManager.setOptions({ drawingControl: false });

            const passToSetPolygon = () => {
              /**
               * Convert the Google Maps Polygon to GeoJSON format.
               * Pass the polygon to the `setPolygon` state action,
               * for use outside of this function.
               */
              const coordinates: google.maps.LatLngLiteral[] = _polygon
                .getPath()
                .getArray()
                .map((_point) => _point.toJSON());
              // Convert to GeoJSON Polygon format.
              // Ensure the polygon is closed by repeating the first point.
              const polygonGeoJSON: Polygon = {
                type: "Polygon",
                coordinates: [
                  coordinates
                    // GeoJSON places longitude first, then latitude. Google Maps does it the other way around.
                    .map((coord) => [coord.lng, coord.lat])
                    .concat([[coordinates[0].lng, coordinates[0].lat]]),
                ],
              };
              props.setPolygon(polygonGeoJSON);
            };

            // Initial capture.
            passToSetPolygon();
            // Add listener for polygon drag and resize.
            _polygon.addListener("mouseup", passToSetPolygon);
          }
        },
      );

      return () => {
        google.maps.event.removeListener(overlayCompleteListener);
      };
    }
  }, [props, drawingManager]);

  return (
    <>
      <Map
        mapId="map-lead-search-create"
        defaultCenter={{
          lat: props.defaultCenter.coordinates[1],
          lng: props.defaultCenter.coordinates[0],
        }}
        defaultZoom={8}
        minZoom={6}
        gestureHandling="greedy"
        controlSize={32}
      />
      <MapControl position={ControlPosition.TOP_CENTER} />
    </>
  );
});
DrawSinglePolygonMap.displayName = "DrawSinglePolygonMap";

export default DrawSinglePolygonMap;
